import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import NavbarIconsGenericComponent from '../generic-nav-bar/NavbarIconsGenericComponent';
import { startLoadingEndUserActivity } from '../../app/actions/statisticActions';
import { isEndUserActivityReportsEnabled } from '../../app/helpers/flowEngineConfigHelper';

const formatWhen = (value) => {
  if (!value) return '—';
  return String(value).replace('T', ' ').slice(0, 16);
};

const CountBarList = ({ rows, labelKey, empty }) => {
  const max = Math.max(0, ...(rows || []).map((row) => Number(row.total) || 0));
  if (!rows?.length) {
    return <p className="text-muted small mb-0">{empty}</p>;
  }
  return (
    <div className="mb-3">
      {rows.map((row) => {
        const pct = max ? Math.round((Number(row.total) / max) * 100) : 0;
        return (
          <div className="mb-2" key={row[labelKey] || row.type || row.day}>
            <div className="d-flex justify-content-between small">
              <span>{row[labelKey] || row.type || row.day}</span>
              <span>{row.total}</span>
            </div>
            <div className="progress" style={{ height: 8 }}>
              <div className="progress-bar" role="progressbar" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SimpleTable = ({ columns, rows, empty }) => {
  if (!rows?.length) {
    return <p className="text-muted small mb-0">{empty}</p>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm table-striped">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || index}>
              {columns.map((col) => (
                <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ListEndUserActivity = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configuration = useSelector((state) => state.configuration);
  const activity = useSelector((state) => state.statistic.endUserActivity);
  const enabled = isEndUserActivityReportsEnabled(configuration);

  useEffect(() => {
    if (enabled) {
      dispatch(startLoadingEndUserActivity());
    }
  }, [dispatch, enabled]);

  if (!enabled) {
    return (
      <>
        <NavbarIconsGenericComponent componentName="endUserActivity" />
        <div className="container mt-4">
          <p className="text-muted">
            {t('endUser.activity.disabled', { defaultValue: 'Estos reportes están desactivados.' })}
          </p>
        </div>
      </>
    );
  }

  const summary = activity?.summary || {};

  return (
    <>
      <NavbarIconsGenericComponent componentName="endUserActivity" />
      <div className="container mt-4 pb-5">
        <h2>{t('endUser.activity.title', { defaultValue: 'Mi actividad' })}</h2>
        <p className="text-muted small">
          {t('endUser.activity.hint', { defaultValue: 'Últimos 90 días de búsquedas, consultas y soportes. Sin datos de otros usuarios.' })}
        </p>

        <div className="row mb-4">
          {[
            { key: 'searches', label: t('endUser.activity.searches', { defaultValue: 'Búsquedas' }) },
            { key: 'views', label: t('endUser.activity.views', { defaultValue: 'Casos consultados' }) },
            { key: 'compares', label: t('endUser.activity.compares', { defaultValue: 'Comparaciones' }) },
            { key: 'supports', label: t('endUser.activity.supports', { defaultValue: 'Soportes' }) }
          ].map((item) => (
            <div className="col-md-3" key={item.key}>
              <div className="border rounded p-3 mb-2">
                <div className="small text-muted">{item.label}</div>
                <div className="fs-4">{summary[item.key] ?? 0}</div>
              </div>
            </div>
          ))}
        </div>

        <h4>{t('endUser.activity.byType', { defaultValue: 'Intenciones por tipo' })}</h4>
        <CountBarList
          rows={activity?.byEventType}
          labelKey="type"
          empty={t('endUser.activity.emptyTypes', { defaultValue: 'Sin intenciones registradas.' })}
        />

        <h4 className="mt-4">{t('endUser.activity.searchesByDay', { defaultValue: 'Búsquedas por día' })}</h4>
        <CountBarList
          rows={activity?.searchesByDay}
          labelKey="day"
          empty={t('endUser.activity.emptyDays', { defaultValue: 'Sin búsquedas en el periodo.' })}
        />

        <h4 className="mt-4">{t('endUser.activity.viewsByDay', { defaultValue: 'Consultas por día' })}</h4>
        <CountBarList
          rows={activity?.viewsByDay}
          labelKey="day"
          empty={t('endUser.activity.emptyViewDays', { defaultValue: 'Sin consultas de fichas en el periodo.' })}
        />

        <h4 className="mt-4">{t('endUser.activity.lastSearches', { defaultValue: 'Últimas búsquedas' })}</h4>
        <SimpleTable
          empty={t('endUser.activity.emptySearches', { defaultValue: 'Aún no hay búsquedas.' })}
          columns={[
            { key: 'text_search', label: t('endUser.activity.text', { defaultValue: 'Texto' }) },
            { key: 'created_at', label: t('endUser.activity.when', { defaultValue: 'Fecha' }), render: (row) => formatWhen(row.created_at) }
          ]}
          rows={activity?.searches}
        />

        <h4 className="mt-4">{t('endUser.activity.consulted', { defaultValue: 'Casos que consulté' })}</h4>
        <SimpleTable
          empty={t('endUser.activity.emptyViews', { defaultValue: 'Aún no hay consultas de fichas.' })}
          columns={[
            { key: 'module_key', label: t('endUser.activity.module', { defaultValue: 'Módulo' }) },
            { key: 'origin_reference_id', label: t('endUser.activity.case', { defaultValue: 'Caso' }) },
            { key: 'search_text', label: t('endUser.activity.text', { defaultValue: 'Texto' }) },
            { key: 'created_at', label: t('endUser.activity.when', { defaultValue: 'Fecha' }), render: (row) => formatWhen(row.created_at) }
          ]}
          rows={activity?.views}
        />

        <h4 className="mt-4">{t('endUser.activity.lastSupports', { defaultValue: 'Mis soportes' })}</h4>
        <SimpleTable
          empty={t('endUser.activity.emptySupports', { defaultValue: 'Aún no hay tickets de soporte.' })}
          columns={[
            { key: 'module_key', label: t('endUser.activity.module', { defaultValue: 'Módulo' }) },
            { key: 'status', label: t('endUser.activity.status', { defaultValue: 'Estado' }) },
            { key: 'comments', label: t('endUser.activity.comment', { defaultValue: 'Comentario' }) },
            { key: 'created_at', label: t('endUser.activity.when', { defaultValue: 'Fecha' }), render: (row) => formatWhen(row.created_at) }
          ]}
          rows={activity?.supports}
        />
      </div>
    </>
  );
};

export default ListEndUserActivity;
